import { type CSATRating } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * Experience Feedback Atoms
 *
 * State management for the Evidence Experience Feedback nudge.
 * This feature captures holistic user satisfaction (1-5 rating + optional comment)
 * triggered randomly between the 3rd and 10th completed query per session.
 *
 * @see docs/design-docs/experience-feedback/README.md
 */

// localStorage keys
export const EXPERIENCE_FEEDBACK_SUBMITTED_KEY = 'heidi:experience-feedback-submitted';
const SNOOZE_COUNT_KEY = 'heidi:experience-feedback-snooze-count';

// Trigger threshold - show nudge after this many completed queries
export const TRIGGER_QUERY_COUNT = 2;

// ─────────────────────────────────────────────────────────────────────────────
// Persisted Atoms (synced to localStorage)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Whether the user has ever submitted experience feedback.
 * Once true, the nudge will never be shown again.
 */
export const experienceFeedbackSubmittedAtom = atomWithStorage<boolean>(
  EXPERIENCE_FEEDBACK_SUBMITTED_KEY,
  false
);

/**
 * Number of times the user has clicked "Maybe later" (snoozed).
 * After 3 snoozes, the nudge will never be shown again.
 */
export const experienceFeedbackSnoozeCountAtom = atomWithStorage<number>(
  SNOOZE_COUNT_KEY,
  0
);

// ─────────────────────────────────────────────────────────────────────────────
// Session Atoms (not persisted - reset on page refresh)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Whether the nudge is currently visible.
 * Controlled by trigger logic in useExperienceFeedbackTrigger.
 */
export const experienceFeedbackVisibleAtom = atom<boolean>(false);

/**
 * Ease rating (1-5) - "How easy was it to use Evidence?"
 * Uses CSATRating enum for consistency with existing CSAT patterns.
 */
export const experienceFeedbackRatingAtom = atom<CSATRating | null>(null);

/**
 * Usefulness rating (1-5) - "How useful was Evidence for you?"
 */
export const experienceFeedbackUsefulnessRatingAtom = atom<CSATRating | null>(null);

/**
 * Free text comment from the user (optional).
 */
export const experienceFeedbackDetailAtom = atom<string>('');

/**
 * Whether the nudge has been triggered this session.
 * Prevents the nudge from appearing multiple times in a single session
 * even if the user dismisses it (different from snooze).
 */
export const experienceFeedbackTriggeredThisSessionAtom = atom<boolean>(false);

/**
 * Whether the rating has been submitted this session.
 * Used for the two-phase UI:
 * - Phase 1 (false): Show rating stars only, "Maybe later" button
 * - Phase 2 (true): Show textarea and Submit button for qualitative feedback
 *
 * This enables progressive disclosure: rating is captured immediately on star click,
 * then user can optionally provide qualitative feedback.
 */
export const experienceFeedbackRatingSubmittedAtom = atom<boolean>(false);

// ─────────────────────────────────────────────────────────────────────────────
// Derived Atoms
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Whether the nudge should be eligible to show based on persistence state.
 * True if: user has not submitted AND snooze count < 3
 *
 * Note: This doesn't account for session-level conditions (query count, streaming, etc.)
 * Those are handled by useExperienceFeedbackTrigger.
 */
export const experienceFeedbackEligibleAtom = atom((get) => {
  const hasSubmitted = get(experienceFeedbackSubmittedAtom);
  const snoozeCount = get(experienceFeedbackSnoozeCountAtom);

  return !hasSubmitted && snoozeCount < 3;
});

// ─────────────────────────────────────────────────────────────────────────────
// Action Atoms
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Snooze the feedback nudge.
 * Increments snooze count and hides the nudge.
 */
export const experienceFeedbackSnoozeAtom = atom(null, (get, set) => {
  const currentCount = get(experienceFeedbackSnoozeCountAtom);
  set(experienceFeedbackSnoozeCountAtom, currentCount + 1);
  set(experienceFeedbackVisibleAtom, false);
  set(experienceFeedbackTriggeredThisSessionAtom, true);
});

/**
 * Mark feedback as submitted.
 * Sets submitted flag to true and hides the nudge forever.
 */
export const experienceFeedbackMarkSubmittedAtom = atom(null, (_, set) => {
  set(experienceFeedbackSubmittedAtom, true);
  set(experienceFeedbackVisibleAtom, false);
});

/**
 * Dismiss the nudge without snoozing (just hide for this session).
 * The nudge can still appear in future sessions if eligible.
 */
export const experienceFeedbackDismissAtom = atom(null, (_, set) => {
  set(experienceFeedbackVisibleAtom, false);
  set(experienceFeedbackTriggeredThisSessionAtom, true);
});

/**
 * Show the feedback nudge.
 */
export const experienceFeedbackShowAtom = atom(null, (_, set) => {
  set(experienceFeedbackVisibleAtom, true);
  set(experienceFeedbackTriggeredThisSessionAtom, true);
});

/**
 * Reset the form state (ease rating, usefulness rating, detail, and rating submitted status).
 * Called when the nudge is dismissed or after submission.
 */
export const experienceFeedbackResetFormAtom = atom(null, (_, set) => {
  set(experienceFeedbackRatingAtom, null);
  set(experienceFeedbackUsefulnessRatingAtom, null);
  set(experienceFeedbackDetailAtom, '');
  set(experienceFeedbackRatingSubmittedAtom, false);
});
